/**
 * CUPOLAS (2026-10-09, docs/bim-plan/29-cupola-study.md; Matt: "a similar way to do cupolas at
 * roofs inspired by the same architectural makers" — Forest Studio, Jeffrey Dungan).
 *
 * The rules, in one place, so the geometry, the palette and the cut list read one source. Pure:
 * every input is a number the caller already has (the geometry layout reads the roof — ridge
 * run, pitch, the attic under it — and calls these). Feet throughout.
 *
 * Matt's decisions (2026-10-09):
 *   - functional is the norm (light / vent), decorative allowed and said so plainly;
 *   - a LIGHT cupola cuts a real opening through the roof AND the ceiling below, the shaft
 *     straight by default with a flare as an option;
 *   - a VENT cupola in a fire zone: metal louvers backed by 1/8" metal mesh (CWUIC §504.10.2),
 *     free area derated by 2.25;
 *   - finials and weathervanes, every ornament the study listed, none of them rudimentary.
 */

const IN = 1 / 12

export type CupolaPlan = 'square' | 'octagon' | 'monitor'
export const CUPOLA_PLANS: { key: CupolaPlan; label: string }[] = [
  { key: 'square', label: 'Square' },
  { key: 'octagon', label: 'Octagon' },
  { key: 'monitor', label: 'Ridge Monitor (long, runs with the ridge)' },
]
export type CupolaBase = 'saddle' | 'kick' | 'curb'
export const CUPOLA_BASES: { key: CupolaBase; label: string }[] = [
  { key: 'saddle', label: 'Saddle (cut to the pitch)' },
  { key: 'kick', label: 'Kicked (flares onto the roof)' },
  { key: 'curb', label: 'Cheek Walls on a Curb' },
]
export type CupolaStage = 'louver' | 'glazed' | 'operable' | 'leaded' | 'open' | 'solid'
export const CUPOLA_STAGES: { key: CupolaStage; label: string }[] = [
  { key: 'louver', label: 'Louvers' },
  { key: 'glazed', label: 'Divided-Light Sash' },
  { key: 'operable', label: 'Awning Sash (opens)' },
  { key: 'leaded', label: 'Leaded Diamond Lights' },
  { key: 'open', label: 'Open (posts only)' },
  { key: 'solid', label: 'Solid Panel' },
]
export type CupolaRoof = 'pyramid' | 'pagoda' | 'bell' | 'cone' | 'gable' | 'dome'
export const CUPOLA_ROOFS: { key: CupolaRoof; label: string }[] = [
  { key: 'pyramid', label: 'Pyramid / Hip' },
  { key: 'pagoda', label: 'Pagoda (concave, flared eave)' },
  { key: 'bell', label: 'Bell (convex, necked)' },
  { key: 'cone', label: 'Cone / Witch’s Hat' },
  { key: 'gable', label: 'Gable' },
  { key: 'dome', label: 'Dome' },
]
export type CupolaClad = 'board' | 'shingle' | 'panel' | 'metal'
export const CUPOLA_CLADS: { key: CupolaClad; label: string }[] = [
  { key: 'board', label: 'Board and Batten' },
  { key: 'shingle', label: 'Cedar Shingle' },
  { key: 'panel', label: 'Painted Panel' },
  { key: 'metal', label: 'Copper / Bronze Clad' },
]
export type CupolaFinial = 'none' | 'ball' | 'spire' | 'vane'
export const CUPOLA_FINIALS: { key: CupolaFinial; label: string }[] = [
  { key: 'none', label: 'None' },
  { key: 'ball', label: 'Ball Finial' },
  { key: 'spire', label: 'Ball and Spire' },
  { key: 'vane', label: 'Weathervane' },
]
export type VaneOrnament = 'arrow' | 'rooster' | 'horse' | 'sailboat' | 'fish' | 'roosterArrow'
export const VANE_ORNAMENTS: { key: VaneOrnament; label: string }[] = [
  { key: 'arrow', label: 'Arrow' },
  { key: 'rooster', label: 'Rooster' },
  { key: 'roosterArrow', label: 'Rooster over Arrow' },
  { key: 'horse', label: 'Running Horse' },
  { key: 'sailboat', label: 'Sailboat' },
  { key: 'fish', label: 'Fish' },
]
export type CupolaPurpose = 'light' | 'vent' | 'decorative'
export const CUPOLA_PURPOSES: { key: CupolaPurpose; label: string }[] = [
  { key: 'light', label: 'Light (opens to the room below)' },
  { key: 'vent', label: 'Vent (attic exhaust)' },
  { key: 'decorative', label: 'Decorative (does nothing)' },
]

/** the cupola the tool starts on for each firm style (the awning precedent) */
export const CUPOLA_STYLE_DEFAULTS: Record<string, string> = {
  'Modern Farmhouse': 'Great-Room Lantern',
  'Mountain Modern': 'Barn Vent Cupola',
  'Organic Modern': 'Ridge Light Monitor',
  'California Modern': 'Ridge Light Monitor',
  'Modern Spanish': 'Octagonal Leaded Lantern',
}

/** the sizing rule: inches of cupola per foot of unbroken ridge (JLC 1¼; Rockford 1; Good Directions 1½) */
export const CUPOLA_RATIO_DEFAULT = 1.25
/** how many on one ridge run: one, two past 32', three past 64' (Hansen; FS Nantucket) */
export function cupolaCountFor(ridgeFt: number): 1 | 2 | 3 {
  return ridgeFt > 64 ? 3 : ridgeFt > 32 ? 2 : 1
}
/** the width a cupola takes off its share of the ridge, snapped to whole 2" (feet) */
export function cupolaWidthFor(ridgeFt: number, ratioInPerFt: number, count = 1): number {
  const w = (ratioInPerFt * (ridgeFt / Math.max(1, count))) * IN
  return Math.max(18 * IN, Math.min(8, Math.round(w / (2 * IN)) * 2 * IN))
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const pick = <T extends string>(v: unknown, list: { key: T }[], d: T): T => (list.some((x) => x.key === v) ? v as T : d)

/** the look, read off the type (instance overrides already merged by the caller) */
export interface CupolaLook {
  plan: CupolaPlan; base: CupolaBase; stage: CupolaStage; roof: CupolaRoof; clad: CupolaClad
  finial: CupolaFinial; ornament: VaneOrnament; cornice: 'none' | 'fascia' | 'full'
  /** the cupola roof's own pitch, rise per 12 (planar roofs) */
  roofRise: number
  /** solid base above the ridge, × width (Montana: 30" on 6'-0") */
  baseRatio: number
  /** the light / louver stage, × width */
  stageRatio: number
  /** a monitor's length along the ridge, × width */
  lengthRatio: number
}
export function cupolaLook(tp: Record<string, unknown>): CupolaLook {
  const plan = pick(tp.plan, CUPOLA_PLANS, 'square')
  return {
    plan,
    base: pick(tp.base, CUPOLA_BASES, 'saddle'),
    stage: pick(tp.stage, CUPOLA_STAGES, 'glazed'),
    roof: pick(tp.roofForm, CUPOLA_ROOFS, plan === 'monitor' ? 'gable' : 'pyramid'),
    clad: pick(tp.cladding, CUPOLA_CLADS, 'board'),
    finial: pick(tp.finial, CUPOLA_FINIALS, 'ball'),
    ornament: pick(tp.vaneOrnament, VANE_ORNAMENTS, 'arrow'),
    cornice: tp.cornice === 'none' || tp.cornice === 'fascia' ? tp.cornice : 'full',
    roofRise: num(tp.roofRise) ?? (plan === 'monitor' ? 6 : 9),
    baseRatio: num(tp.baseRatio) ?? 0.42,
    stageRatio: num(tp.stageRatio) ?? 0.55,
    lengthRatio: num(tp.lengthRatio) ?? 2.4,
  }
}

/** every height of a cupola W wide (feet), ridge-relative: z = 0 at the ridge's roof surface */
export interface CupolaProportions {
  W: number; L: number
  baseTop: number; belt: number; stageBot: number; stageTop: number
  frieze: number; cornice: number; eaveZ: number; overhang: number; roofH: number; apexZ: number
  /** corner board / post width */
  post: number
}
export function cupolaProportions(look: CupolaLook, W: number): CupolaProportions {
  const L = look.plan === 'monitor' ? Math.max(W * look.lengthRatio, W + 5) : W
  const baseTop = Math.max(look.base === 'curb' ? 8 * IN : 10 * IN, W * look.baseRatio)
  const belt = look.plan === 'monitor' ? 0 : Math.max(2 * IN, W * 0.035)
  const stageBot = baseTop + belt
  const stageTop = stageBot + Math.max(10 * IN, W * look.stageRatio)
  const frieze = look.cornice === 'full' ? Math.max(3.5 * IN, W * 0.07) : look.cornice === 'fascia' ? Math.max(2.5 * IN, W * 0.045) : 0
  const cornice = look.cornice === 'full' ? Math.max(1.5 * IN, W * 0.035) : look.cornice === 'fascia' ? 0.75 * IN : 0
  const eaveZ = stageTop + frieze + (look.cornice === 'full' ? cornice * 0.9 : 0)
  const overhang = look.roof === 'gable' ? Math.max(3 * IN, W * 0.08) : Math.max(1.5 * IN, W * 0.03)
  const half = W / 2 + cornice + overhang
  const rise = half * look.roofRise / 12
  const roofH = look.roof === 'pagoda' ? Math.max(rise, W * 0.55)
    : look.roof === 'bell' ? Math.max(rise, W * 0.6)
    : look.roof === 'cone' ? Math.max(rise, W * 0.75)
    : look.roof === 'dome' ? half * 0.85
    : rise
  return { W, L, baseTop, belt, stageBot, stageTop, frieze, cornice, eaveZ, overhang, roofH, apexZ: eaveZ + roofH, post: Math.max(2.5 * IN, W * 0.075) }
}

/** a curved roof's profile: t 0 (eave) → 1 (apex) to [radius fraction, height fraction] */
export function cupolaRoofProfile(form: CupolaRoof, t: number): [number, number] {
  if (form === 'pagoda') return [1 - t, Math.pow(t, 1.75)]
  if (form === 'bell') return [0.08 + 0.92 * Math.pow(Math.cos(t * Math.PI / 2), 0.55), t]
  if (form === 'dome') return [Math.cos(t * Math.PI / 2), Math.sin(t * Math.PI / 2)]
  return [1 - t, t]
}

// ---------- function: vent, light, fire ----------

/** louver free-area factors (gross ÷ factor): louvers + ¼" cloth 2.0, + ⅛" mesh 2.25, + 1/16" mesh 3.0 */
export const LOUVER_NFA_FACTOR = { quarter: 2.0, eighth: 2.25, sixteenth: 3.0 }

/** the louver stage's free area, in² — gross clear opening ÷ the mesh factor, capped by the throat */
export function cupolaVentFreeArea(look: CupolaLook, p: CupolaProportions, fireZone: boolean): { grossSqIn: number; nfaSqIn: number; throatSqIn: number } {
  const rail = Math.max(2 * IN, p.W * 0.055)
  const clearH = Math.max(0, p.stageTop - p.stageBot - 2 * rail)
  let gross = 0
  if (look.plan === 'octagon') {
    const side = p.W * 0.4142
    gross = 8 * Math.max(0, side - 2 * Math.max(1.5 * IN, p.W * 0.03)) * clearH
  } else {
    const faceW = Math.max(0, p.W - 2 * p.post), faceL = Math.max(0, p.L - 2 * p.post)
    gross = 2 * (faceW + faceL) * clearH
  }
  const grossSqIn = gross * 144
  const factor = fireZone ? LOUVER_NFA_FACTOR.eighth : LOUVER_NFA_FACTOR.quarter
  const louvered = look.stage === 'louver' ? grossSqIn / factor : 0
  const throat = cupolaThroat(p, 'vent')
  const throatSqIn = throat.w * throat.l * 144
  return { grossSqIn, nfaSqIn: Math.min(louvered, throatSqIn), throatSqIn }
}

/** the hole through the roof (and, for light, the shaft size): vent = 1" inside the base walls
 *  (cut sheathing only — 3½" to 6" margin, the install-sheet range; a 24" unit gets a ~16" hole,
 *  Lomanco's rated 24" cupola cuts 17"); light = inside the 2x6 cheek walls */
export function cupolaThroat(p: CupolaProportions, purpose: CupolaPurpose): { w: number; l: number } {
  const m = purpose === 'vent' ? Math.min(6 * IN, Math.max(3.5 * IN, p.W * 0.12 + IN)) : 5.5 * IN
  return { w: Math.max(0, p.W - 2 * m), l: Math.max(0, p.L - 2 * m) }
}

/** CRC R806.2 with the 1/300 exception: half the area high, within 3' of the ridge — in² */
export function requiredHighVentSqIn(atticSf: number): number {
  return Math.max(0, atticSf) * 144 / 300 * 0.5
}

export interface CupolaCheck {
  purpose: CupolaPurpose; fireZone: boolean
  ridgeFt: number | null; count: number; ratioInPerFt: number | null
  pitch: number; atticSf: number | null; widthManual: boolean
  /** the roof also has a ridge vent */
  ridgeVent?: boolean
  /** a glass cupola roof (a skylight) */
  glassRoof?: boolean
}
export interface CupolaReport {
  warnings: string[]
  notes: string[]
  nfaSqIn: number
  needSqIn: number
}
export function cupolaReport(look: CupolaLook, p: CupolaProportions, c: CupolaCheck): CupolaReport {
  const warnings: string[] = [], notes: string[] = []
  if (c.ratioInPerFt != null) {
    if (c.ratioInPerFt < 0.98) warnings.push(`Undersized for its ridge: ${c.ratioInPerFt.toFixed(2)}" per foot. Below 1" per foot a cupola reads as an afterthought.`)
    else if (c.ratioInPerFt > 1.6) warnings.push(`Large for its ridge: ${c.ratioInPerFt.toFixed(2)}" per foot. Consider two smaller cupolas along the ridge.`)
  } else warnings.push('Not on a ridge: a cupola off the ridge on a slope wider than 30" needs a cricket on its uphill side (CRC R903.2.2).')
  if (p.W > 4 + 1e-6 || p.L > 12 + 1e-6) warnings.push('Wider than 4\'-0": an engineer sizes the anchorage (wind on a tall box, CRC R301.1.3).')
  if (c.pitch > 9 && look.base !== 'curb') notes.push('Steeper than 9:12: the saddle base is built taller than a stock base allows.')
  const vent = cupolaVentFreeArea(look, p, c.fireZone)
  const need = c.atticSf != null ? requiredHighVentSqIn(c.atticSf) : 0
  if (c.purpose === 'vent') {
    if (look.stage !== 'louver') warnings.push('A vent cupola needs louvers. This one has ' + (CUPOLA_STAGES.find((s) => s.key === look.stage)?.label.toLowerCase() ?? 'no louvers') + '.')
    const total = vent.nfaSqIn * c.count
    if (c.atticSf != null && total < need) warnings.push(`Free area ${Math.round(total)} in² is short of the ${Math.round(need)} in² the attic needs up high (CRC R806.2, 1/300 with half high). Go bigger, or add high vents elsewhere.`)
    else notes.push(`Vents ${Math.round(total)} in²${c.atticSf != null ? ` against ${Math.round(need)} in² needed up high` : ''}. Soffit intake still required.`)
    if (c.ridgeVent) warnings.push('This roof also has a ridge vent: mixed exhaust short-circuits. Stop the ridge vent short of the cupola, or make the cupola the only exhaust.')
    if (c.fireZone) notes.push('Fire zone: metal louvers backed by 1/8" noncombustible mesh (CWUIC §504.10.2); free area derated by 2.25. Plan check may ask for a listed vent.')
  }
  if (c.purpose === 'light') {
    const t = cupolaThroat(p, 'light')
    notes.push(`Opens the roof and the ceiling below: a ${fmtFt(t.w)}${look.plan === 'monitor' ? ` × ${fmtFt(t.l)}` : ' square'} shaft, walls insulated like exterior walls (R-13 min.).`)
    if (look.stage === 'louver' || look.stage === 'solid') warnings.push('A light cupola needs glass. This one has ' + (look.stage === 'louver' ? 'louvers' : 'solid panels') + '.')
    if (look.stage === 'operable' && c.fireZone) notes.push('Fire zone: an open sash lets embers in. Screen it with metal mesh or keep it shut in red-flag weather.')
  }
  if (c.purpose === 'decorative') notes.push('Decorative: cuts nothing, vents nothing, lights nothing. It sits on the ridge by choice.')
  if (c.glassRoof) notes.push('Glass more than 15° from vertical is a skylight: laminated (0.030" interlayer).')
  if (look.finial === 'vane') notes.push('Weathervane rod embeds 8–9" in a headblock; directionals set to true north. Bond it to a lightning protection system if the house has one (NFPA 780).')
  return { warnings, notes, nfaSqIn: vent.nfaSqIn, needSqIn: need }
}

function fmtFt(ft: number): string {
  const inch = Math.round(ft * 12 * 4) / 4
  const f = Math.floor(inch / 12 + 1e-9), r = inch - f * 12
  return f ? `${f}'-${Number.isInteger(r) ? r : r.toFixed(2).replace(/0$/, '')}"` : `${r}"`
}

export interface CupolaCutRow { part: string; size: string; qty: number; note?: string }
/** the shop list the palette shows (approximate, at design stage) */
export function cupolaCutList(look: CupolaLook, p: CupolaProportions, purpose: CupolaPurpose, roofingName: string): CupolaCutRow[] {
  const rows: CupolaCutRow[] = []
  const faces = look.plan === 'octagon' ? 8 : 4
  rows.push({ part: look.base === 'curb' ? 'Curb + cheek walls' : 'Saddle base', size: look.base === 'curb' ? '2x6 on 2x curb' : '¾" ext. ply skirt on 2x4 blocks', qty: 1, note: fmtFt(p.W) + (look.plan === 'monitor' ? ' × ' + fmtFt(p.L) : look.plan === 'octagon' ? ' oct' : ' sq') })
  rows.push({ part: 'Corner posts', size: p.W > 4 ? '4x4 (engineer)' : p.W > 3 ? '4x4' : '2x4', qty: faces === 8 ? 8 : 4 })
  const stageLabel = { louver: 'Louver panel, 45° blades, ¼" overlap', glazed: 'Fixed divided-light sash, IG', operable: 'Awning sash on chain stays, IG', leaded: 'Leaded diamond sash', open: 'Open bay', solid: 'Solid raised panel' }[look.stage]
  rows.push({ part: 'Stage', size: stageLabel, qty: look.plan === 'monitor' ? 2 * Math.max(2, Math.round(p.L / 3.5)) + 2 : faces })
  if (look.cornice !== 'none') rows.push({ part: look.cornice === 'full' ? 'Frieze + crown' : 'Fascia', size: 'painted PVC / Accoya', qty: 1 })
  rows.push({ part: 'Roof', size: roofingName, qty: 1, note: CUPOLA_ROOFS.find((r) => r.key === look.roof)?.label })
  if (look.finial !== 'none') rows.push({ part: look.finial === 'vane' ? 'Weathervane assembly' : 'Finial', size: look.finial === 'vane' ? 'copper ornament, SS rod, directionals' : 'turned copper', qty: 1 })
  if (purpose === 'vent') rows.push({ part: 'Mesh', size: '⅛" noncombustible metal', qty: faces })
  if (purpose === 'light') rows.push({ part: 'Light shaft', size: '2x4 walls, R-13, ½" gyp', qty: 1 }, { part: 'Headers', size: 'doubled rafter + joist headers', qty: 4 })
  return rows
}
